import { cn } from "@/design-system/lib/utils";

/**
 * One thing that needs a look, as a dot-and-count outline pill that is also the
 * filter.
 *
 * <p>🔴 <b>Outline, not filled.</b> An exception is an annotation on rows that
 * still exist, not a status those rows now have — the same reason
 * <c>ExceptionBadge</c> is an outline. Filled pills on a young book make an
 * ordinary list look like a wall of problems, and by the time a real exception
 * appears the reader has stopped seeing pills.</p>
 *
 * <p><b>Amber for attention, red for overdue</b> — not "red for bad". A thing
 * that needs a look this week and a thing that breached its date are different,
 * and using one colour for both means a reader cannot triage without reading
 * every row. Amber uses the INK token: the fill-weight amber is 2.55:1 as small
 * text on its own tint, under AA.</p>
 *
 * <p>A pill at ZERO renders muted and stays clickable-off rather than
 * disappearing, so the vocabulary of things being watched is stable from one
 * visit to the next — a pill that vanishes when it is satisfied teaches the
 * reader nothing about what is being checked.</p>
 */
export interface AttentionItem {
  key: string;
  label: string;
  count: number;
  severity?: "attention" | "overdue";
}

export function AttentionTile({
  items,
  selectedKey,
  onSelect,
  loading,
}: {
  items: AttentionItem[];
  selectedKey?: string;
  /** Called with the pill's key, or `null` when the active one is clicked again. */
  onSelect: (key: string | null) => void;
  loading?: boolean;
}) {
  return (
    <section aria-label="Needs attention" data-testid="attention-pills">
      <h3 className="font-display text-label font-semibold uppercase tracking-label text-gray-mid m-0 mb-space-2">
        Needs attention
      </h3>
      <ul className="m-0 p-0 list-none flex flex-wrap gap-space-2">
        {items.map((item) => {
          const active = selectedKey === item.key;
          const empty = !loading && item.count === 0;
          const overdue = item.severity === "overdue";
          return (
            <li key={item.key}>
              <button
                type="button"
                aria-pressed={active}
                onClick={() => onSelect(active ? null : item.key)}
                data-testid={`attention-${item.key}`}
                className={cn(
                  "inline-flex items-center gap-space-1 rounded-pill border bg-transparent",
                  "px-space-3 py-[3px] text-caption font-medium leading-none",
                  "transition-colors duration-fast ease-standard",
                  "focus-visible:outline-none focus-visible:shadow-goldRing",
                  empty
                    ? "border-border-light text-gray-mid"
                    : overdue
                      ? "border-status-error text-status-error"
                      : "border-status-warning-ink text-status-warning-ink",
                  active && "bg-onyx/5 font-semibold",
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    "inline-block h-[6px] w-[6px] rounded-full",
                    empty
                      ? "bg-border-light"
                      : overdue
                        ? "bg-status-error"
                        : "bg-status-warning-ink",
                  )}
                />
                <span>{item.label}</span>
                <span className="tabular-nums">{loading ? "—" : item.count}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
